import * as React from 'react';

interface TextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  label?: string;
  error?: string;
  fullWidth?: boolean;
  className?: string;
  inputClassName?: string;
  minRows?: number;
  maxRows?: number;
  /** Счётчик символов внутри поля (правый нижний угол) */
  characterCount?: { current: number; max: number };
}

function getLineHeightPx(el: HTMLElement): number {
  const style = getComputedStyle(el);
  const lineHeight = parseFloat(style.lineHeight);
  if (!Number.isNaN(lineHeight)) return lineHeight;
  const fontSize = parseFloat(style.fontSize) || 16;
  return fontSize * 1.25;
}

function getInputHeightPx(): number | null {
  const val = getComputedStyle(document.documentElement).getPropertyValue('--input-height').trim();
  if (!val) return null;
  const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  if (val.endsWith('rem')) return parseFloat(val) * root;
  if (val.endsWith('px')) return parseFloat(val);
  return null;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({
  label,
  error,
  fullWidth,
  className = '',
  inputClassName = '',
  id,
  minRows = 1,
  maxRows,
  value,
  onChange,
  characterCount,
  ...props
}, ref) {
  const inputId = id ?? `textarea-${Math.random().toString(36).slice(2)}`;
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const resolvedRef = (node: HTMLTextAreaElement | null) => {
    (textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
  };

  React.useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el || maxRows == null) return;
    const style = getComputedStyle(el);
    const lineH = getLineHeightPx(el);
    const paddingTop = parseFloat(style.paddingTop) || 0;
    const paddingBottom = parseFloat(style.paddingBottom) || 0;
    const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const minH = Math.max(
      paddingTop + paddingBottom + minRows * lineH,
      getInputHeightPx() ?? 0
    );
    const maxH = paddingTop + paddingBottom + maxRows * lineH;

    const inputH = getInputHeightPx();
    let h: number;
    if (value === '' || value == null) {
      h = inputH != null && minRows <= 1 ? Math.min(minH, inputH) : minH;
      el.style.overflowY = 'hidden';
    } else {
      el.style.minHeight = '0';
      el.style.height = '0';
      const scrollH = el.scrollHeight;
      h = Math.min(Math.max(scrollH, minH), maxH);
      el.style.overflowY = scrollH > maxH ? 'auto' : 'hidden';
    }

    const minHCap = inputH != null && minRows <= 1 && (value === '' || value == null) ? Math.min(minH, inputH) : minH;
    const isEmptySingleLine = (value === '' || value == null) && minRows <= 1 && inputH != null;
    if (isEmptySingleLine) {
      el.style.height = `${inputH}px`;
      el.style.minHeight = `${inputH}px`;
    } else {
      el.style.height = `${h / root}rem`;
      el.style.minHeight = `${minHCap / root}rem`;
    }
  }, [value, minRows, maxRows]);

  const taClass = `form__input form__input_textarea ${characterCount ? 'form__input_textarea_has-counter' : ''} ${error ? 'form__input_error' : ''} ${inputClassName}`;

  const textareaEl = (
    <textarea
      ref={resolvedRef}
      id={inputId}
      className={taClass}
      style={maxRows != null ? { overflow: 'hidden', resize: 'none', minHeight: 0 } : undefined}
      aria-invalid={!!error}
      aria-describedby={error ? `${inputId}-error` : undefined}
      value={value}
      onChange={onChange}
      {...props}
    />
  );

  return (
    <div className={`form__field ${fullWidth ? 'form__field_full' : ''} ${className}`}>
      {label && (
        <label htmlFor={inputId} className="form__label">
          {label}
        </label>
      )}
      {characterCount ? (
        <div className="form__textarea-with-counter">
          {textareaEl}
          <span className="form__textarea-counter" aria-live="polite">
            {characterCount.current}/{characterCount.max}
          </span>
        </div>
      ) : (
        textareaEl
      )}
      {error && (
        <span id={`${inputId}-error`} className="form__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
});
